/** @format */

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import styled from 'styled-components'
import { _$, Button, Tag, Message } from '@king-design/react'
import { MonitorSearch } from 'src/components'
import TablePage from 'src/components/TablePage'
import useFormTable from 'src/hooks/useFormTable'
import { InstanceItem } from '../constants'

// 全部实例组件的props
export interface AllInstanceTabProps {
  className?: string
  policyId: string
  productType: string
  cdnFilter: boolean
  selectedInstance: InstanceItem[]
  setSelectedInstance: (value: InstanceItem[]) => void
  api: string
  params: any
  region: string
  instanceIdsTotal: number
  setInstaneIdsTotal: (val: number) => void
}

export interface AllInstanceTabHandle {
  getUnbindList?: () => string[]
}
const AllInstanceTab = forwardRef<AllInstanceTabHandle, AllInstanceTabProps>(
  (
    {
      className,
      policyId,
      cdnFilter,
      selectedInstance,
      setSelectedInstance,
      api,
      params,
      region,
      instanceIdsTotal,
      setInstaneIdsTotal
    },
    ref
  ) => {
    const [checkedKeys, setCheckedKeys] = useState<string[]>([])
    const [searchValue, setSearchValue] = useState<string>('')
    const [tableData, setTableData] = useState<InstanceItem[]>([])
    const [tabelInfo, setTabelInfo] = useState<boolean>(false)
    const [bindList, setBindList] = useState<string[]>([])
    const [unbindList, setUnbindList] = useState<string[]>([])
    // 表格状态筛选
    const [group, setGroup] = useState<{ thresholdBindStatus: number }>({ thresholdBindStatus: 0 })

    useEffect(() => {
      setTableData([])
      if (api && params) {
        run()
      }
    }, [api, params, cdnFilter])

    useEffect(() => {
      setSearchValue('')
    }, [region])

    const { run, result, total, pageState, loading } = useFormTable(
      api,
      {
        manual: true,
        params: cdnFilter
          ? {
              Metric: 'cdn.vod.hitpv',
              Query: searchValue,
              FilterPolicyBindStatus: group.thresholdBindStatus,
              ...params
            }
          : {
              Query: searchValue,
              FilterPolicyBindStatus: group.thresholdBindStatus,
              ...params
            },
        method: api?.includes('DescribeMonitorInstances') ? 'GET' : 'POST',
        page: !api?.includes('DescribeMonitorInstances')
      },
      data => {
        return {
          tableData: data?.resourceList,
          totalCount: data?.totalCount
        }
      }
    )

    useEffect(() => {
      if (result) {
        setTableData(result)
        const properties = result.find(item => {
          return item?.properties?.length
        })
        setTabelInfo(!!properties)
      }
    }, [result])

    const getScheme = () => {
      const allScheme = {
        instanceName: {
          title: _$('实例名称')
        },
        instanceId: {
          title: _$('实例ID')
        },
        instanceIP: {
          title: _$('IP地址')
        },
        properties: {
          title: _$('备注'),
          // minWidth: 600,
          template({ properties }) {
            return properties?.length
              ? properties.map(({ name, value }) =>
                  name && value ? (
                    <p style={{ margin: 0 }}>
                      {name}：{value}
                    </p>
                  ) : (
                    ''
                  )
                )
              : ''
          }
        },
        thresholdBindStatus: {
          title: _$('状态'),
          width: 150,
          group: [
            { label: _$('全部'), value: 0 },
            { label: _$('已绑定'), value: 1 },
            { label: _$('未绑定'), value: 2 }
          ],
          template(data) {
            return data?.thresholdBindStatus === 1 ? (
              <Tag type="primary" size="mini">
                {_$('已绑定')}
              </Tag>
            ) : (
              <Tag size="mini">{_$('未绑定')}</Tag>
            )
          }
        },
        active: {
          title: _$('操作'),
          width: 100,
          template(data) {
            const { thresholdBindStatus, instanceId } = data
            const bindFind = bindList.find(item => item === instanceId)
            const unbindFind = unbindList.find(item => item === instanceId)
            return (!bindFind && !thresholdBindStatus) || unbindFind ? (
              <a
                className="primary-color"
                onClick={() => {
                  addInstances([instanceId])
                  Message.warning(_$('操作新建，点击确认后生效'))
                }}
              >
                <Button type="primary" size="small">
                  {_$('新建')}
                </Button>
              </a>
            ) : (
              <a
                className="primary-color"
                onClick={() => {
                  removeInstances([instanceId])
                  Message.warning(_$('操作解绑，点击确认后生效'))
                }}
              >
                <Button type="secondary" size="small">
                  {_$('解绑')}
                </Button>
              </a>
            )
          }
        }
      }
      if (!policyId) {
        delete allScheme.thresholdBindStatus
      }
      if (!tabelInfo) {
        delete allScheme.properties
      }
      return allScheme
    }
    // 新建实例
    const addInstances = (data = checkedKeys) => {
      if (tableData?.length && data?.length) {
        const update = data.map(key => {
          return tableData.find(({ instanceId }) => instanceId === key)
        })
        setBindList([...bindList, ...data])
        setInstaneIdsTotal && setInstaneIdsTotal(instanceIdsTotal + update.length)
        setSelectedInstance(selectedInstance.concat(update))
        setCheckedKeys([])
      }
    }

    // 解绑实例
    const removeInstances = (data = checkedKeys) => {
      if (tableData?.length && data?.length) {
        const update = selectedInstance.filter(item => !data.includes(item.instanceId))
        setSelectedInstance([...update])
        setUnbindList([...unbindList, ...data])
        const bindFilter = bindList.filter(item => !data.includes(item))
        setBindList(bindFilter)
        setInstaneIdsTotal && setInstaneIdsTotal(instanceIdsTotal - data.length)
        setCheckedKeys([])
      }
    }

    useImperativeHandle(ref, () => ({
      getUnbindList: () => {
        return unbindList
      }
    }))

    // 批量绑定button状态
    const bindBtnDisabled = () => {
      if (checkedKeys?.length) {
        const result = checkedKeys.every((instanceId, index) => {
          const find = tableData.find(item => item.instanceId === instanceId)
          return (
            (find?.thresholdBindStatus === 0 && !bindList.includes(instanceId)) ||
            unbindList.includes(instanceId)
          )
        })
        return !result
      } else {
        return true
      }
    }

    useEffect(() => {
      run({
        Status: group.thresholdBindStatus,
        PageIndex: 1
      })
    }, [group])

    // 批量解绑button状态
    const unbindBtnDisabled = () => {
      if (checkedKeys?.length) {
        const result = checkedKeys.every((instanceId, index) => {
          const find = tableData.find(item => item.instanceId === instanceId)
          return (
            (find?.thresholdBindStatus === 1 && !unbindList.includes(instanceId)) ||
            bindList.includes(instanceId)
          )
        })
        return !result
      } else {
        return true
      }
    }

    return (
      <div className={className}>
        <div className="filter-table">
          <Button
            type="primary"
            disabled={bindBtnDisabled()}
            onClick={() => {
              addInstances()
              Message.warning(_$('操作新建，点击确认后生效'))
            }}
          >
            {_$('批量新建')}
          </Button>
          <Button
            disabled={unbindBtnDisabled()}
            onClick={() => {
              removeInstances()
              Message.warning(_$('操作解绑，点击确认后生效'))
            }}
          >
            {_$('批量解绑')}
          </Button>
          <MonitorSearch
            placeholder={_$('输入实例ID、实例名称、实例信息')}
            value={searchValue}
            onChange={value => {
              setSearchValue(value)
              run({
                Query: value,
                PageIndex: 1
              })
            }}
          />
        </div>
        <TablePage
          showPage={true}
          tableProps={{
            data: tableData,
            scheme: getScheme(),
            resizable: true,
            rowKey: item => item.instanceId,
            onChangeCheckedKeys: item => {
              setCheckedKeys(item)
            },
            checkedKeys,
            rowCheckable: false,
            group: group,
            onChangeGroup: group => {
              setGroup(group)
            }
          }}
          pageProps={{ limits: [10, 20, 50, 100], total: total, ...pageState }}
          loading={loading}
          run={run}
        />
      </div>
    )
  }
)

export default styled(AllInstanceTab)`
  .table-page {
    margin-top: 8px;
    max-width: 1200px;
  }
`
